import { RefObject, useLayoutEffect, useState } from "react";

export default function useAppointmentFormContextLayout(
  layoutRef: RefObject<HTMLDivElement>,
  contextRef: RefObject<HTMLDivElement>,
  expandedPanelCount: number,
) {
  const [isPinned, setIsPinned] = useState(false);
  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const context = contextRef.current;
    const scroller = document.getElementById("rootOutletContent");
    if (!layout || !context || !scroller) return;

    let frame: number | undefined;
    let pinFrame: number | undefined;
    const observed = new Set<Element>();
    const setLength = (name: string, value: number) => {
      const length = `${Math.ceil(value)}px`;
      if (layout.style.getPropertyValue(name) !== length) layout.style.setProperty(name, length);
    };
    const updatePinned = () => {
      const distance = context.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      setIsPinned((wasPinned) => distance <= (wasPinned ? 2 : 0.5));
    };
    const schedulePinned = () => {
      if (pinFrame !== undefined) return;
      pinFrame = requestAnimationFrame(() => {
        pinFrame = undefined;
        updatePinned();
      });
    };
    const measure = () => {
      const title = layout.querySelector("h2");
      const footer = layout.querySelector('[data-testid="defaultForm-action-bar"]');
      const summaries = context.querySelectorAll(".MuiAccordionSummary-root");
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const gap = rem * 0.25;
      const titleHeight = title?.getBoundingClientRect().height || rem * 3;
      const footerHeight = footer?.getBoundingClientRect().height || rem * 4;
      const headerHeight = Array.from(summaries).reduce((height, summary) => height + summary.getBoundingClientRect().height, 0);
      const formSpace = Math.min(rem * 12, scroller.clientHeight * 0.4);
      const bodyBudget = Math.max(0, scroller.clientHeight - titleHeight - footerHeight - formSpace - headerHeight - gap * 3);
      const renderedPanelCount = context.querySelectorAll('.MuiAccordionDetails-root:not([hidden])').length; // exiting bodies must retain their height until the collapse transition finishes
      setLength("--appointment-context-body-max-height", bodyBudget / (renderedPanelCount || expandedPanelCount || 1));
      setLength("--appointment-context-height", context.getBoundingClientRect().height);
      setLength("--appointment-form-title-height", titleHeight);
      updatePinned();
      [title, footer, ...summaries].forEach((element) => {
        if (element && !observed.has(element)) {
          observed.add(element);
          observer?.observe(element);
        }
      });
    };
    const scheduleMeasure = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = undefined;
        measure();
      });
    };
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(() => {
      setLength("--appointment-context-height", context.getBoundingClientRect().height); // keep the sticky title attached to the animated panels before paint
      scheduleMeasure();
    });
    [layout, context, scroller].forEach((element) => observer?.observe(element));
    measure();
    window.addEventListener("resize", scheduleMeasure);
    scroller.addEventListener("scroll", schedulePinned, { passive: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      scroller.removeEventListener("scroll", schedulePinned);
      if (frame !== undefined) cancelAnimationFrame(frame);
      if (pinFrame !== undefined) cancelAnimationFrame(pinFrame);
    };
  }, [layoutRef, contextRef, expandedPanelCount]);
  return isPinned;
}
